"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Image from "next/image";
import ServicedevCards from "../development/page";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

const text = "Details of our services & solutions";

export default function ServicedevelopmentCards() {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);

  return (
    <div className="mt-30 mb-20 space-y-16 pt-28 overflow-x-hidden sm:pt-10 md:pt-6 lg:pt-0">
      <ServicedevCards />

      <div className="mt-6"></div>

      <div
        className="mt-20 px-4 lg:mx-auto lg:max-w-7xl"
        id="development-financing"
      >
        <div className="py-10">
          {/* Why Developmental Financing is Crucial */}
          <div className="mb-20 grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div>
              <h3 className="mb-6 text-3xl font-bold" style={{ color: colors.primary }}>
                Why Developmental Financing is Crucial
              </h3>
              <p className="mb-8 text-lg" style={{ color: colors.textDark }}>
                YMFI's Development Finance Initiative plays a vital role in
                promoting financial inclusion for underserved communities. By
                addressing key challenges in cash liquidity, training resources,
                and risk-sharing partnerships, we ensure sustainable growth and
                effectively support Ethiopia's marginalized groups in achieving
                economic empowerment.
              </p>

              <div className="rounded-lg p-6" style={{ backgroundColor: `${colors.primary}10` }}>
                <h4 className="mb-4 font-bold" style={{ color: colors.primary }}>
                  Critical Strengths in Development Finance:
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 h-5 w-5 rounded-full" style={{ backgroundColor: colors.primaryLight }}></div>
                    <span style={{ color: colors.textDark }}>Promotes inclusive economic growth</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 h-5 w-5 rounded-full" style={{ backgroundColor: colors.primaryLight }}></div>
                    <span style={{ color: colors.textDark }}>Targets underserved market segments</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 h-5 w-5 rounded-full" style={{ backgroundColor: colors.primaryLight }}></div>
                    <span style={{ color: colors.textDark }}>Drives sustainable development goals</span>
                  </li>
                  <li className="flex items-start">
                    <div className="mr-3 mt-1 h-5 w-5 rounded-full" style={{ backgroundColor: colors.primaryLight }}></div>
                    <span style={{ color: colors.textDark }}>Strengthens community resilience</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="rounded-xl border p-8 shadow-lg" style={{ borderColor: `${colors.primary}10`, backgroundColor: colors.white }}>
              <h4 className="mb-6 text-2xl font-bold" style={{ color: colors.primary }}>
                Partners in Development Finance
              </h4>
              <div className="grid grid-cols-2 gap-6">
                {[
                  "Government Agencies",
                  "International NGOs",
                  "Local Cooperatives",
                  "Private Sector",
                  "Community Groups",
                  "Financial Institutions",
                ].map((partner, index) => (
                  <div key={index} className="flex items-center">
                    <div className="mr-3 h-3 w-3 rounded-full" style={{ backgroundColor: colors.primaryLight }}></div>
                    <span style={{ color: colors.textDark }}>{partner}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Specialized Loan Products */}
          <div className="mb-20">
            <h3 className="mb-12 text-center text-3xl font-bold" style={{ color: colors.primary }}>
              Our Development Finance Products
            </h3>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  title: "Urban Agriculture Loan",
                  description: "Boosts green initiatives and urban farming projects",
                  icon: "🌱",
                },
                {
                  title: "Etege (Women's Finance)",
                  description: "Targets female entrepreneurs with favorable terms",
                  icon: "👩‍💼",
                },
                {
                  title: "Eshete (Youth Loan)",
                  description: "Empowers young entrepreneurs to establish businesses",
                  icon: "🧑‍🎓",
                },
                {
                  title: "Disability Loan",
                  description: "Financial inclusion for entrepreneurs with disabilities",
                  icon: "♿",
                },
                {
                  title: "Green Energy Loan",
                  description: "Supports sustainable energy adoption",
                  icon: "🌞",
                },
                {
                  title: "Innovation Startup Loan",
                  description: "Funds innovative new business ventures",
                  icon: "💡",
                },
                {
                  title: "Public Association Loan",
                  description: "Funds social development and community projects",
                  icon: "🏛️",
                },
                {
                  title: "Water & Sanitation Loan",
                  description: "Improves access to clean water and facilities",
                  icon: "💧",
                },
                {
                  title: "Idea Financing",
                  description: "Turns innovative ideas into viable businesses",
                  icon: "✨",
                },
              ].map((product, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="rounded-xl border p-6 shadow-md transition-shadow hover:shadow-lg"
                  style={{ borderColor: `${colors.primary}10`, backgroundColor: colors.white }}
                >
                  <div className="mb-4 text-4xl">{product.icon}</div>
                  <h4 className="mb-2 text-xl font-bold" style={{ color: colors.primary }}>
                    {product.title}
                  </h4>
                  <p style={{ color: colors.textDark }}>{product.description}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Financing Process */}
          <div className="mb-20">
            <h3 className="mb-12 text-center text-3xl font-bold" style={{ color: colors.primary }}>
              Our Financing Process
            </h3>

            <div className="relative">
              {/* Timeline connector */}
              <div className="absolute left-1/2 top-12 hidden h-[calc(100%-6rem)] w-0.5 -translate-x-1/2 transform md:block" style={{ backgroundColor: `${colors.primary}20` }}></div>

              <div className="grid grid-cols-1 gap-12 md:grid-cols-4 md:gap-6">
                {[
                  {
                    title: "Consultation",
                    description: "Initial needs assessment and project evaluation",
                    step: "1",
                  },
                  {
                    title: "Application",
                    description: "Submit required documents and business plan",
                    step: "2",
                  },
                  {
                    title: "Approval",
                    description: "Fast-track underwriting and risk assessment",
                    step: "3",
                  },
                  {
                    title: "Disbursement",
                    description: "Funds released for project implementation",
                    step: "4",
                  },
                ].map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative flex flex-col items-center text-center"
                  >
                    {/* Step indicator */}
                    <div className="relative z-10 mb-4 flex h-12 w-12 items-center justify-center rounded-full text-lg font-bold text-white" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})` }}>
                      {step.step}
                    </div>

                    {/* Content */}
                    <div className="px-4">
                      <h4 className="mb-2 text-xl font-bold" style={{ color: colors.primary }}>
                        {step.title}
                      </h4>
                      <p style={{ color: colors.textDark }}>{step.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Success Stories */}
          <div className="mb-20">
            <h3 className="mb-12 text-center text-3xl font-bold" style={{ color: colors.primary }}>
              Success Stories
            </h3>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              {[
                {
                  title: "Addis Urban Farm",
                  description: "Used Urban Agriculture Loan to establish hydroponic farming",
                  image: "/projects/agricult.png",
                },
                {
                  title: "Green Energy Startup",
                  description: "Solar panel installation with Green Energy Loan",
                  image: "/projects/greenenergy27.jpg",
                },
                {
                  title: "Women's Textile Cooperative",
                  description: "Etege financing for equipment and training",
                  image: "/projects/textile.jpg",
                },
              ].map((project, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group overflow-hidden rounded-xl shadow-lg"
                >
                  <div className="relative h-48">
                    <Image
                      src={project.image}
                      alt={project.title}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${colors.primary}CC, transparent)` }}></div>
                  </div>
                  <div className="p-6" style={{ backgroundColor: colors.white }}>
                    <h4 className="mb-2 text-xl font-bold" style={{ color: colors.primary }}>
                      {project.title}
                    </h4>
                    <p className="mb-4" style={{ color: colors.textDark }}>{project.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Yegna other unique loan */}
      <div
        className="mt-20 px-4 lg:mx-auto lg:max-w-[1500px]"
        id="special-loans"
      ></div>
    </div>
  );
}